import { useQuery } from "@tanstack/react-query";
import type { ElementBlueprint, ElementBlueprintCatalog } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { manualApi } from "../../../api/client.js";

/**
 * The realm's AR element blueprint catalogue — what the pickers render (AR P1 read / P3 pickers).
 *
 * Fetched lazily (`enabled`) so opening the editor does not pay for a catalogue the author may never open. It is
 * realm-scoped and effectively static, so it is cached generously; a new blueprint is a DatabaseManager operation,
 * not something that changes mid-session.
 */
export function useElementBlueprints(session: AuthSession | null, manualId: number, enabled: boolean): { data: ElementBlueprintCatalog | null; isPending: boolean; isError: boolean } {
  const q = useQuery({
    queryKey: ["element-blueprints", session?.realm, manualId],
    enabled: enabled && session !== null,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<ElementBlueprintCatalog> => {
      const res = await manualApi.listElementBlueprints({ params: { id: manualId } });
      if (res.status !== 200) throw new Error(`blueprint catalogue failed (${String(res.status)})`);
      return res.body;
    },
  });
  return { data: q.data ?? null, isPending: q.isPending, isError: q.isError };
}

/**
 * The MAIN-ELEMENT tiles, in the legacy's fixed order.
 *
 * `ARLMS_SettingUIs.js:61` pins the order with a literal five-name array — and pointedly does NOT include the newer
 * `_v4` generation, so those fall through unranked and are appended after. That is why the legacy dialog shows the
 * four classic tiles first and the six "Dunkel" ones after, rather than pairing each layout with its dark variant.
 * Matched literally here for the same reason: this ordering is verifiable against the legacy UI, and a cleverer
 * family-based interleave would silently diverge from it.
 *
 * Anything beyond the array keeps its place at the end, sorted by label — so takeda's own `Hauptelement Text links`
 * (3854 steps) and the whole `_v4` generation are shown, never hidden for not being named in a 2023 array.
 *
 * NOT translatable: these are blueprint names as stored in the DATABASE, matched against `blueprint.name`.
 * Translating them would break the ordering silently — the array would stop matching anything.
 */
const MAIN_ORDER: readonly string[] = [
  "MainElement_v2",
  "Checklist Main Element",
  "Question Main Element",
  "Interaction Main Element",
  "Sensor Main Element",
];

/** Rank a blueprint by the legacy tile order; unranked ones sort after, alphabetically. */
function rank(name: string): number {
  const i = MAIN_ORDER.indexOf(name);
  return i === -1 ? MAIN_ORDER.length : i;
}

/**
 * The `MainElements` tiles a given manual may choose from.
 *
 * **Placeable only, with no superadmin override** — the legacy hard-filters this picker (`ARLMS_SettingUIs.js:87`),
 * unlike the XR-element one, because a main element that cannot be placed cannot carry a step. The **sensor** tile is
 * hidden unless the manual is a sensor manual; the server enforces the same rule, since a picker is a convenience and
 * never a gate.
 */
export function mainElementTiles(catalog: ElementBlueprintCatalog | null, isSensorManual: boolean): ElementBlueprint[] {
  const cat = catalog?.categories.find((c) => c.name === "MainElements");
  if (!cat) return [];
  return cat.blueprints
    .filter((bp) => bp.isPlaceable)
    .filter((bp) => isSensorManual || !/^Sensor/i.test(bp.name))
    .slice()
    .sort((a, b) => rank(a.name) - rank(b.name) || (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name));
}

/** The label a tile shows: `displayName` when it has one, else the internal `name` (the legacy's own fallback). */
export function blueprintLabel(bp: ElementBlueprint): string {
  const d = bp.displayName?.trim();
  return d != null && d !== "" ? d : bp.name;
}

/**
 * The categories the `+` picker offers, alphabetically, each with its blueprints.
 *
 * **`SceneCenter` and `MainElements` are hidden entirely** (`ARLMS_BlueprintSelection.js:22-25`): the first is the
 * manual-level settings carrier, the second belongs to the `Auswählen` picker and a step has exactly one. `Sensoren`
 * appears only on a sensor manual. Categories with nothing left to show are dropped, so takeda renders
 * Arrows / Content / Hands rather than its five empty ones.
 *
 * Alphabetical because `ar_element_category."order"` is **0 for every category in both realms** — the legacy sorts the
 * same way (`categoryArray.sort()`), for the same reason.
 */
export function stepElementCategories(
  catalog: ElementBlueprintCatalog | null,
  opts: { isSensorManual: boolean; showNonPlaceable: boolean; allowSensorCategory?: boolean },
): Array<{ name: string; blueprints: ElementBlueprint[] }> {
  if (catalog == null) return [];
  return catalog.categories
    .filter((c) => c.name !== "SceneCenter" && c.name !== "MainElements")
    // A sensor element belongs to a STEP, never to "Alle Schritte" (ADR-038 D10) — the server refuses it there, so
    // offering the category on that target is a grid of dead ends (diff panel).
    .filter((c) => (opts.isSensorManual && (opts.allowSensorCategory ?? true)) || c.name !== "Sensoren")
    .map((c) => ({
      name: c.name,
      blueprints: c.blueprints
        // An UNAVAILABLE DEVICE is not a "non-placeable template": it is a real device the author registered, which
        // simply has no element to place it with. D4's whole point is that they are SAID, not swallowed — and the
        // switch that reveals non-placeable templates is superadmin-only, so gating them behind it would keep them
        // silent for nearly everyone (diff panel).
        .filter((bp) => bp.isPlaceable || bp.unavailableReason != null || opts.showNonPlaceable)
        .sort((x, y) => blueprintLabel(x).localeCompare(blueprintLabel(y))),
    }))
    .filter((c) => c.blueprints.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
}
